import { useEffect, useCallback } from 'react';

/**
 * Keyboard shortcuts for the Functions page
 * 
 * N - New function
 * R - Refresh list
 * / - Focus search
 * Delete - Delete selected function
 * Enter - Edit selected function
 * Escape - Close modal / clear selection
 */
const useKeyboardShortcuts = ({
  // Modal states
  isModalOpen,
  // Actions
  onNew,
  onRefresh,
  onFocusSearch,
  onDeleteSelected,
  onEditSelected,
  onClearSelection,
  // Selection
  selectedFunctionId,
  selectedIds,
}) => {
  const handleKeyDown = useCallback((event) => {
    // Don't handle shortcuts when typing in inputs
    const target = event.target;
    const isInputFocused = 
      target.tagName === 'INPUT' || 
      target.tagName === 'TEXTAREA' || 
      target.isContentEditable ||
      target.closest('.cm-editor'); // CodeMirror

    // Always handle Escape
    if (event.key === 'Escape') {
      if (isModalOpen) {
        // Modal will handle its own close
        return;
      }
      if (selectedFunctionId || (selectedIds && selectedIds.size > 0)) {
        event.preventDefault();
        onClearSelection?.();
        return;
      }
    }

    // Skip other shortcuts when in input
    if (isInputFocused) return;

    // Skip when modal is open
    if (isModalOpen) return;

    switch (event.key.toLowerCase()) {
      case 'n':
        event.preventDefault();
        onNew?.();
        break;

      case 'r':
        event.preventDefault();
        onRefresh?.();
        break;

      case '/':
        event.preventDefault();
        onFocusSearch?.();
        break;

      case 'delete':
      case 'backspace':
        if (selectedFunctionId || (selectedIds && selectedIds.size > 0)) {
          event.preventDefault();
          onDeleteSelected?.();
        }
        break;

      case 'enter':
        if (selectedFunctionId) {
          event.preventDefault();
          onEditSelected?.();
        }
        break;

      default:
        break;
    }
  }, [
    isModalOpen,
    selectedFunctionId,
    selectedIds,
    onNew,
    onRefresh,
    onFocusSearch,
    onDeleteSelected,
    onEditSelected,
    onClearSelection,
  ]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
};

export default useKeyboardShortcuts;
